/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Explicit-language filter preference.
 *
 * Mirrors GH's ExplicitLanguageFilterModal setting: when the filter is
 * ON, tracks whose `parentalSettings.hasExplicitLanguage` is `true`
 * render as masked / non-interactive rows. GH persists the choice on
 * the user profile (`UserPreferences.isExplicitLanguageAllowed`); we
 * cache the resolved value locally so pages can query it synchronously
 * without a round-trip on every render. `isExplicitLanguageAllowed`
 * from the wire is inverted into "filter on / off" for the toggle UI —
 * filter ON means explicit language is NOT allowed.
 *
 * localStorage seeds the observable at startup; when the user is
 * signed in the wire mutation is authoritative and its result
 * overwrites the local cache.
 */

import {
    asData,
    combineData,
    data,
    externalData,
    type MaybeObservableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { gqlFetch } from '@/service/gql'
import { getAuth } from '@/auth/authRef'
import { globalRef } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { toast } from '@/components/toast'
import { getStringAsync } from '@/explicitLanguageFilter/i18n/bundle.generated'
import { globalData, gql } from '@amzn/amp-playback'

/** localStorage key for the filter toggle. `true` = filter on. */
export const EXPLICIT_FILTER_KEY = 'explicitLanguageFilter'

const { errorHandler } = ctx('explicitLanguageFilter')

function readLocal(): boolean {
    try {
        return localStorage.getItem(EXPLICIT_FILTER_KEY) === 'true'
    } catch {
        return false
    }
}

function writeLocal(next: boolean): void {
    try {
        if (next) {
            localStorage.setItem(EXPLICIT_FILTER_KEY, 'true')
        } else {
            localStorage.removeItem(EXPLICIT_FILTER_KEY)
        }
    } catch {
        /* private-mode Safari — ignore. */
    }
}

const state$ = globalData(() => data(readLocal()))

/**
 * Observable: `true` when explicit content should be filtered / masked.
 * Subscribe from long-lived call sites (e.g. row renderers) so the UI
 * updates when the user flips the toggle.
 */
export const explicitLanguageFilter$: ObservableValue<boolean> = state$

/** Snapshot: `true` when explicit content should be filtered. */
export function isExplicitLanguageFilterOn(): boolean {
    return state$.value
}

/**
 * Whether the explicit-filter settings toggle should be shown (hidden for
 * child/age-restricted profiles the account holder locks). The underlying gate
 * lives in `childProfile`, which carries the profile-type probe and is kept off
 * the first-paint chunk. This is the boot-eager bridge: it lazy-loads and
 * subscribes `childProfile` only when a caller actually subscribes (e.g. the
 * header menu renders its toggle row), so the boot path never pulls the probe
 * in. Fail-safe hidden until the profile resolves.
 */
export const canSetExplicitLanguageFilter$: ObservableValue<boolean> = externalData<boolean>(
    false,
    (setData) => {
        let dispose: (() => void) | undefined
        let disposed = false
        import('@/auth/childProfile')
            .then((m) => {
                // The subscriber may have already unsubscribed while this chunk
                // was loading — subscribing anyway would leak, since nothing
                // else will ever call the dispose assigned below.
                if (disposed) return
                dispose = m.canSetExplicitLanguageFilter$.onData(setData)
            })
            .catch(() => {
                // Chunk load failed — leave the gate fail-safe (toggle hidden).
            })
        return () => {
            disposed = true
            dispose?.()
        }
    }
)

/**
 * Inform the user why a play action just no-op'd — the explicit-language
 * filter masks it. GH shows a modal offering to unblock; we deliberately
 * show only an informational toast pointing at the account-menu toggle
 * (no unblock affordance). Every masked play surface calls this so the
 * message is defined once.
 */
export function toastExplicitBlocked(): void {
    toast(getStringAsync('dmusic_explicit_filter_blocked_toast'))
}

/**
 * Prompt the user when a masked play action no-op'd, forking on the reason
 * (mirroring GH's `promptForElfPlayback`):
 *
 * - age-band restricted (child/teen) → route to the age-restricted modal,
 *   which offers NO unblock affordance (the parent controls the filter);
 * - otherwise → the standard informational toast, which the customer can
 *   act on via the account-menu toggle.
 *
 * The age reason, DM engine, and trigger enum are all read through one lazy
 * `import()` so this boot-reachable service keeps the child-profile probe, the
 * DM subtree, and the trigger enum off the first-paint chunk — they load only
 * when a masked play is actually attempted. Every masked-play site calls this
 * so the fork is defined once.
 */
export function promptExplicitBlocked(): void {
    Promise.all([
        import('@/auth/childProfile'),
        import('@/messaging/dm/dynamicMessagingEngine'),
        import('@/messaging/dm/types'),
    ])
        .then(
            ([{ ageRestrictedReason$ }, { dynamicMessagingEngine }, { DynamicMessageTrigger }]) => {
                // `ageRestrictedReason$` only ever carries an age-band reason (or
                // null), so a present value IS the age-restricted case: route to
                // the no-unblock modal. A null/adult reason falls through to the
                // actionable toast.
                if (ageRestrictedReason$.value == null) {
                    toastExplicitBlocked()
                    return
                }
                return dynamicMessagingEngine.showDynamicMessageForTrigger(
                    DynamicMessageTrigger.AGE_RESTRICTED_ACTION
                )
            }
        )
        .catch(() => {
            // Chunk load / show failed — fall back to the informational toast
            // so the tap is never a silent dead-end.
            toastExplicitBlocked()
        })
}

/**
 * Derives whether a given entity is masked under the filter: the
 * filter is ON *and* the entity is flagged explicit. Track rows,
 * album/playlist heros, and any other play surface share this so the
 * "masked ⇒ inert play affordance" contract is defined in one place
 * rather than re-spelled per component. Pass a plain boolean or an
 * observable of the entity's `parentalSettings.hasExplicitLanguage`.
 */
export function isExplicitMasked$(
    explicit: MaybeObservableValue<boolean>
): ObservableValue<boolean> {
    return combineData({ filter: explicitLanguageFilter$, explicit: asData(explicit) }).map(
        ({ filter, explicit }) => filter && explicit
    )
}

// language=GraphQL
const updateUserExplicitLanguageFilterMutation = gql(`
    mutation updateUserExplicitLanguageFilter($isExplicitLanguageAllowed: Boolean!) {
        updateUserExplicitLanguageFilter(isExplicitLanguageAllowed: $isExplicitLanguageAllowed) {
            preferences {
                isExplicitLanguageAllowed
            }
        }
    }
`)

const writesRef = globalRef(() => ({ latest: 0, inFlight: 0, confirmed: false, confirmedWrite: 0 }))

function applyFilter(filterOn: boolean): void {
    state$.value = filterOn
    writeLocal(filterOn)
}

/**
 * Persist the toggle. Updates local cache + observable immediately
 * (optimistic), then fires the GraphQL mutation for signed-in users and
 * rejects when it fails. Overlapping writes reconcile once they have all
 * settled: to the echo of the newest write that succeeded, or to the value
 * held before the first of them when none did. Enabling it (OFF→ON) also
 * clears the live queue, which may hold now-masked explicit content.
 */
export async function writeExplicitLanguageFilter(filterOn: boolean): Promise<void> {
    const wasOn = state$.value
    applyFilter(filterOn)

    // Clear on the OFF→ON transition only — not a redundant enable, and not the
    // server/hydration reconcile (that path is setFilterFromServer). Playback is
    // lazy-imported so it stays off the boot chunk.
    if (filterOn && !wasOn) {
        import('@/blackbolt/playback')
            .then(({ getPlaybackDeps }) => getPlaybackDeps()?.playlistController.clear())
            .catch(errorHandler)
    }

    if (!getAuth().isSignedIn()) return

    const writes = writesRef.value
    if (writes.inFlight === 0) {
        writes.confirmed = wasOn
        writes.confirmedWrite = 0
    }
    writes.inFlight++
    const write = ++writes.latest
    try {
        const result = await gqlFetch(updateUserExplicitLanguageFilterMutation, {
            // Wire is inverted: `isExplicitLanguageAllowed` = !filterOn.
            isExplicitLanguageAllowed: !filterOn,
        })
        const allowed =
            result.updateUserExplicitLanguageFilter?.preferences?.isExplicitLanguageAllowed
        if (write > writes.confirmedWrite) {
            writes.confirmed = typeof allowed === 'boolean' ? !allowed : filterOn
            writes.confirmedWrite = write
        }
    } finally {
        if (--writes.inFlight === 0) applyFilter(writes.confirmed)
    }
}

/**
 * Apply a filter value resolved from the server sync, reconciling both
 * the observable and the local cache. The service stays the sole owner
 * of `state$` / `writeLocal`; the off-boot sync module calls this. While
 * writes are in flight it only replaces their rollback value, and only
 * until one of them confirms.
 */
export function setFilterFromServer(filterOn: boolean): void {
    const writes = writesRef.value
    if (writes.inFlight === 0) applyFilter(filterOn)
    else if (writes.confirmedWrite === 0) writes.confirmed = filterOn
}
